import React, { useState, useEffect } from 'react';
import { useFetching } from 'components/hooks/useFetching';
import HomepageContentService from 'services/HomepageContentService';
import { NotificationManager } from 'react-notifications';
import Strings from 'values/Strings';
import Loader from 'components/UI/loader/Loader';
import CodeEditor from './CodeEditor';
import 'styles/css/admin.css';
import 'styles/css/homepage-content-admin.css';

const HomepageContentManager = () => {
  const [contentList, setContentList] = useState([]);
  const [editingContent, setEditingContent] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '', // Объединяем все в один блок
    is_active: false
  });

  const notificationError = (error) => {
    if (error.length > 0) {
      NotificationManager.error(JSON.stringify(error), Strings.notificationsErrorTitle, 5000);
    }
  };

  const notificationSuccess = (message) => {
    NotificationManager.success(message, Strings.notificationsSuccessTitle, 3000);
  };

  // Загрузка списка контента (локальный лоадер)
  const [fetchingContent, loadingList] = useFetching(async () => {
    try {
      const res = await HomepageContentService.getAllContent();
      setContentList(res);
    } catch (error) {
      notificationError(error);
    }
  }, false);

  // Создание/обновление контента (локальный лоадер)
  const [savingContent, loadingSave] = useFetching(async (data) => {
    try {
      if (editingContent) {
        await HomepageContentService.updateContent(editingContent.id, data);
        notificationSuccess('Контент успешно обновлен');
      } else {
        await HomepageContentService.createContent(data);
        notificationSuccess('Контент успешно создан');
      }
      setEditingContent(null);
      setIsCreating(false);
      setIsModalOpen(false);
      resetForm();
      fetchingContent();
    } catch (error) {
      notificationError(error);
    }
  }, false);

  // Удаление контента (локальный лоадер)
  const [deletingContent, loadingDelete] = useFetching(async (id) => {
    try {
      await HomepageContentService.deleteContent(id);
      notificationSuccess('Контент успешно удален');
      fetchingContent();
    } catch (error) {
      notificationError(error);
    }
  }, false);

  // Активация контента (локальный лоадер)
  const [activatingContent, loadingActivate] = useFetching(async (id) => {
    try {
      await HomepageContentService.activateContent(id);
      notificationSuccess('Контент активирован');
      fetchingContent();
    } catch (error) {
      notificationError(error);
    }
  }, false);

  useEffect(() => {
    fetchingContent();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      is_active: false
    });
  };

  const handleEdit = (content) => {
    setEditingContent(content);
    setFormData({
      title: content.title,
      content: content.content || '',
      is_active: content.is_active
    });
    setIsCreating(false);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingContent(null);
    resetForm();
    setIsCreating(true);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setEditingContent(null);
    setIsCreating(false);
    setIsModalOpen(false);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      NotificationManager.warning('Название контента обязательно', Strings.notificationsWarningTitle, 3000);
      return;
    }

    if (!formData.content.trim()) {
      NotificationManager.warning('Контент не может быть пустым', Strings.notificationsWarningTitle, 3000);
      return;
    }

    const dataToSave = {
      title: formData.title,
      content: formData.content,
      is_active: formData.is_active
    };

    savingContent(dataToSave);
  };

  const handleDelete = (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот контент?')) {
      deletingContent(id);
    }
  };

  const handleActivate = (id) => {
    if (window.confirm('Активировать этот контент? Все остальные будут деактивированы.')) {
      activatingContent(id);
    }
  };

  return (
    <Loader localStatus={loadingList || loadingSave || loadingDelete || loadingActivate}>
      <div className="admin-upload-page-container">
        <div className="admin-section">
          <h2>Управление контентом главной страницы</h2>

          <div className="admin-actions">
            <button
              className="btn btn-primary"
              onClick={handleCreate}
              disabled={loadingSave}
            >
              Создать новый контент
            </button>
          </div>

          {/* Модальное окно для редактора */}
          {isModalOpen && (
            <div className="code-editor-modal">
              <div className="code-editor-modal-content">
                <div className="code-editor-modal-header">
                  <h3 className="code-editor-modal-title">
                    {editingContent ? 'Редактирование контента' : 'Создание нового контента'}
                  </h3>
                  <button
                    className="code-editor-modal-close"
                    onClick={handleCancel}
                  >
                    ×
                  </button>
                </div>

                <div className="code-editor-modal-body">
                  <form onSubmit={handleSubmit} className="admin-form">
                    <div className="form-group">
                      <label>Название контента:</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label>Код (HTML + CSS + JavaScript):</label>
                      <CodeEditor
                        value={formData.content}
                        onChange={(value) => setFormData({ ...formData, content: value })}
                        language="html"
                        placeholder="Введите HTML, CSS и JavaScript код..."
                      />
                      <div className="editor-help">
                        <p><strong>Подсказка:</strong> Можете вставить весь HTML документ целиком, включая теги &lt;style&gt; и &lt;script&gt;. Система автоматически разделит код на части.</p>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        />
                        Активировать сразу после сохранения
                      </label>
                    </div>
                  </form>
                </div>

                <div className="code-editor-modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={loadingSave}
                    onClick={handleSubmit}
                  >
                    {loadingSave ? 'Сохранение...' : 'Сохранить'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Список существующего контента */}
          <div className="admin-list-container">
            <h3>Существующий контент</h3>
            {contentList.length === 0 ? (
              <p>Контент не найден</p>
            ) : (
              <div className="content-list">
                {contentList.map((content) => (
                  <div key={content.id} className={`content-item ${content.is_active ? 'active' : ''}`}>
                    <div className="content-header">
                      <h4>{content.title}</h4>
                      <div className="content-status">
                        {content.is_active && <span className="status-badge active">Активен</span>}
                        <span className="created-by">
                          Создан: {content.name} {content.surname}
                        </span>
                      </div>
                    </div>

                    <div className="content-preview">
                      <div className="preview-section">
                        <strong>Контент:</strong>
                        <pre>{content.content?.substring(0, 300)}...</pre>
                      </div>
                    </div>

                    <div className="content-actions">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEdit(content)}
                        disabled={loadingSave}
                      >
                        Редактировать
                      </button>
                      {!content.is_active && (
                        <button
                          className="btn btn-sm btn-success"
                          onClick={() => handleActivate(content.id)}
                          disabled={loadingActivate}
                        >
                          Активировать
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(content.id)}
                        disabled={loadingDelete}
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Loader>
  );
};

export default HomepageContentManager;
